import React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { mergeSx } from 'ui/merge-sx';

import Button from 'components/partials/button/button';
import Icons from 'assets/icons';

const HEADER_SX: SxProps<Theme> = {
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
};

interface LabelProps {
  children: React.ReactNode;
}

const Label = ({ children }: LabelProps): React.JSX.Element => (
  <Box
    sx={{
      color: 'tokens.navy.500',
      fontSize: '1rem',
      lineHeight: '1.5rem',
      fontWeight: 600,
      mb: 0.5,
    }}
  >
    {children}
  </Box>
);

const Value = ({ children }: LabelProps): React.JSX.Element => (
  <Box
    sx={{
      color: 'tokens.navy.800',
      fontSize: '1.25rem',
      lineHeight: '1.75rem',
      fontWeight: 700,
      textTransform: 'capitalize',
    }}
  >
    {children}
  </Box>
);

interface GoalsManagerHeaderProps {
  type: string | undefined;
  category: string | undefined;
  year: string | number;
  handleNewGoalClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isCreation: boolean;
  isEdition: boolean;
  sx?: SxProps<Theme>;
}

const GoalsManagerHeader = ({
  type,
  category,
  year,
  handleNewGoalClick,
  isCreation,
  isEdition,
  sx,
}: GoalsManagerHeaderProps): React.JSX.Element => (
  <Stack direction="row" sx={mergeSx(HEADER_SX, sx)} data-testid="goal-header">
    <Stack direction="row" sx={{ gap: 3 }}>
      <Box>
        <Label>Category</Label>
        <Value>{category}</Value>
      </Box>
      <Box>
        <Label>Type</Label>
        <Value>{type}</Value>
      </Box>
      <Box>
        <Label>Entry term</Label>
        <Value>{year}</Value>
      </Box>
    </Stack>

    <Button
      variant="brand-filled"
      onClick={handleNewGoalClick}
      disabled={isCreation || isEdition}
      size="lg"
      iconBefore={<Icons.Plus aria-label="PlusIcon" />}
      data-testid="add-goal-button"
    >
      Add a goal
    </Button>
  </Stack>
);

export default GoalsManagerHeader;
